/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import "../../../../firebase";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import Socials from "layouts/authentication/components/Socials";
import Separator from "layouts/authentication/components/Separator";

// Images
import curved6 from "assets/images/curved-images/curved6.jpg";

function Basic() {
  const [agreement, setAgremment] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    title: "",
  });
  
  const showSnackbar = (title, message ) => {
    setSnackbar({ show: true, title, message });
  };

  const hideSnackbar = () => {
    setSnackbar({ ...snackbar, show: false });
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please try another one.';
      case 'auth/weak-password':
        return 'The password is too weak. Please use a stronger password.';
      // ... other error codes ...
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleSetAgremment = () => setAgremment(!agreement);

  const signupHandler = () => {
    event.preventDefault();
    if (!agreement) {
      showSnackbar("Agreement Required:", "Please agree to the Terms and Conditions to proceed.");
      return; 
    }

    const auth = getAuth();
    createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;
        navigate('/pages/wizard');
        console.log("signup succeed:", userCredential)
        // Signed up ///////  

        const userData = {
          name: name,
          email: email,
        };

        fetch('https://services.leadconnectorhq.com/hooks/scTZfdVrJb3bMVHNemTZ/webhook-trigger/c30728d6-7d39-4451-8355-e594943ec743', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('CRM update failed');
          }
          return response.json(); // You can process the response if needed
        })
        .catch((error) => {
          console.error('CRM Update Error:', error);
          // Here, instead of showing an error to the user, log this error to your internal monitoring system
          // For example, you could send this error to a Firebase Function that logs it to Firestore, Cloud Logging, or sends a notification to your team
        });

      })
      .catch((error) => {
        // Error handling
        console.error("error during signup:", error)
        const errorMessage = getErrorMessage(error.code);
        showSnackbar("Signup error:", errorMessage)
      });
  };

    
  return (
    <BasicLayout
      title="Welcome!"
      description="Create your account to get started."
      image={curved6}
    >
      <Card>
        <SoftBox p={3} mb={1} textAlign="center">
          <SoftTypography variant="h5" fontWeight="medium">
            Register with
          </SoftTypography>
        </SoftBox>
        <SoftBox mb={2}>
          <Socials action="signup" />
        </SoftBox>
        <Separator />
        <SoftBox pt={2} pb={3} px={3}>
          <SoftBox component="form" role="form">
            <SoftBox mb={2}>
              <SoftInput placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            </SoftBox>
            <SoftBox mb={2}>
              <SoftInput type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </SoftBox>
            <SoftBox mb={2}>
              <SoftInput type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </SoftBox>
            <SoftBox display="flex" alignItems="center" >
              <Checkbox checked={agreement} onChange={handleSetAgremment} />
              <SoftTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSetAgremment}
                sx={{ cursor: "pointer", userSelect: "none" }}
              >
                &nbsp;&nbsp;I agree to the&nbsp;
              </SoftTypography>
              <SoftTypography
                component="a"
                href="https://prcczxyz.elementor.cloud/elementor-page-185/"
                variant="button"
                fontWeight="bold"
                textGradient
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </SoftTypography>
            </SoftBox>
            <SoftBox mt={4} mb={1}>
              <SoftButton variant="gradient" color="dark" fullWidth onClick={signupHandler}>
                sign up
              </SoftButton>
            </SoftBox>
            <SoftBox mt={3} textAlign="center">
              <SoftTypography variant="button" color="text" fontWeight="regular">
                Already have an account?&nbsp;
                <SoftTypography
                  component={Link}
                  to="/authentication/sign-in/basic"
                  variant="button"
                  color="dark"
                  fontWeight="bold"
                  textGradient
                >
                  Sign in
                </SoftTypography>
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Card>
      <SoftSnackbar
        color="dark"
        icon="notifications"
        title={snackbar.title}
        content={snackbar.message}
        open={snackbar.show}
        close={hideSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
      />
    </BasicLayout>
  );
}

export default Basic;
